import { motion } from "framer-motion";
import { GraduationCap, Footprints, ClipboardCheck, Check, ArrowUpRight } from "lucide-react";

const TRAIN_IMG =
  "https://images.unsplash.com/photo-1543269664-56d93c1b41a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHw0fHxwcm9wZXJ0eSUyMG1hbmFnZXIlMjBvZmZpY2UlMjB0YWJsZXR8ZW58MHx8fHwxNzkwMzQ2MDc4fDA&ixlib=rb-4.1.0&q=85";
const WALK_IMG = "/scopeone-walk-report.png";

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

const TRAIN_FEATURES = [
  "30/60/90-day standardized onboarding tracks",
  "Automated fair-housing & compliance quizzes with timestamps",
  "Digital SOP repository with 1-click role checklists",
  "Frontline Community Operations Certification (COC)",
];

const ProductIcon = ({ icon: Icon, className = "" }) => (
  <span className={`inline-flex h-10 w-10 items-center justify-center rounded-sm border border-cadmium/40 bg-cadmium/10 ${className}`}>
    <Icon size={18} className="text-sky-600" />
  </span>
);

export const Pillars = () => (
  <section id="platform" className="relative py-24 sm:py-32" data-testid="pillars-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div {...reveal} className="max-w-2xl mb-14">
        <p className="eyebrow mb-4 flex items-center gap-3">
          <span className="inline-block h-px w-10 bg-cadmium" /> The Platform
        </p>
        <h2 className="font-cabinet text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">
          Three modules. <span className="font-editorial italic font-normal text-sky-600">One operating standard.</span>
        </h2>
        <p className="mt-4 text-base text-muted-foreground leading-relaxed">
          An owner with 30 communities shouldn't have 30 ways of doing move-ins, inspections, collections, or training.
          OPERA360 gives the organization one standard.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
        <motion.article
          {...reveal}
          data-testid="pillar-train-card"
          className="technical-card lg:col-span-7 p-0 flex flex-col group"
        >
          <div className="relative h-56 sm:h-64 overflow-hidden">
            <img
              src={TRAIN_IMG}
              alt="Property operations manager leading an onboarding training session"
              className="h-full w-full object-cover opacity-70 transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#EDF4FB] via-[#EDF4FB]/40 to-transparent" />
            <span className="absolute top-4 left-4 rounded-sm bg-cadmium px-2.5 py-1 text-[10px] font-mono font-semibold uppercase tracking-widest text-white">
              Flagship
            </span>
          </div>
          <div className="p-6 sm:p-8 flex-1 flex flex-col">
            <div className="flex items-center gap-4 mb-4">
              <ProductIcon icon={GraduationCap} />
              <h3 className="font-cabinet text-2xl sm:text-3xl font-black tracking-tight text-slate-900">TRAIN</h3>
            </div>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
              Onboarding, role-specific learning paths, fair-housing quizzes, SOP video library, and certified mastery.
              Every new manager runs the same playbook from day one.
            </p>
            <ul className="space-y-3 mb-7">
              {TRAIN_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-slate-900/85">
                  <Check size={15} className="mt-0.5 shrink-0 text-green-700" />
                  {f}
                </li>
              ))}
            </ul>
            <div className="mt-auto flex flex-wrap items-center gap-1.5" aria-hidden="true">
              {["Day 30", "Day 60", "Day 90"].map((t, i) => (
                <span
                  key={t}
                  className={`rounded-sm border px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider ${
                    i === 0 ? "border-moss/50 bg-moss/10 text-green-700" : "border-slate-200 text-muted-foreground"
                  }`}
                >
                  {t} {i === 0 ? "✓" : ""}
                </span>
              ))}
              <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-muted-foreground/60">
                Onboarding track — sample
              </span>
            </div>
          </div>
        </motion.article>

        <div className="lg:col-span-5 flex flex-col gap-6 lg:gap-8">
        <motion.article {...reveal} data-testid="pillar-walk-card" className="technical-card p-6 sm:p-8 group">
          <div className="relative h-36 -mx-6 sm:-mx-8 -mt-6 sm:-mt-8 mb-6 overflow-hidden">
            <img
              src={WALK_IMG}
              alt="ScopeOne Walk property walk report with photo documentation, findings, and on-site signature"
              className="h-full w-full object-cover opacity-70 transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#EDF4FB] to-transparent" />
          </div>
          <div className="flex items-center gap-4 mb-3">
            <ProductIcon icon={Footprints} />
            <h3 className="font-cabinet text-xl sm:text-2xl font-bold tracking-tight text-slate-900">WALK</h3>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Powered by <span className="text-slate-900 font-semibold">ScopeOne Walk</span> — the property walk-report
            tool. Time-stamped GPS photo documentation for property, violation, make-ready, move-in/out, and
            owner-tour walks.
          </p>
          <div className="mt-5 grid grid-cols-3 gap-2" data-testid="pillar-walk-structure">
            {["Walk the site", "Sign on the spot", "Send the PDF"].map((s, i) => (
              <div key={s} className="rounded-sm border border-slate-200 bg-slate-50 p-2.5">
                <p className="text-[9px] font-mono text-sky-600">0{i + 1}</p>
                <p className="mt-0.5 text-[10px] font-mono uppercase tracking-wider text-slate-900/85 leading-snug">{s}</p>
              </div>
            ))}
          </div>
          <a
            href="https://www.scopeonewalk.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="pillar-walk-scopeone-link"
            className="mt-4 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-sky-600 hover:text-cadmium-hover transition-colors"
          >
            scopeonewalk.com <ArrowUpRight size={12} />
          </a>
        </motion.article>

        <motion.article {...reveal} data-testid="pillar-audit-card" className="technical-card p-6 sm:p-8">
          <div className="flex items-center gap-4 mb-3">
            <ProductIcon icon={ClipboardCheck} />
            <h3 className="font-cabinet text-xl sm:text-2xl font-bold tracking-tight text-slate-900">AUDIT</h3>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed mb-6">
            Automated regional audit scoring, physical asset degradation indices, and corrective-action task tracking.
          </p>
          <div className="flex items-end gap-1.5 h-16" aria-hidden="true">
            {[42, 58, 51, 70, 66, 84, 92].map((h, i) => (
              <motion.span
                key={i}
                initial={{ height: 0 }}
                whileInView={{ height: `${h}%` }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                className={`flex-1 rounded-sm ${i >= 5 ? "bg-cadmium" : "bg-white/15"}`}
              />
            ))}
          </div>
          <p className="mt-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground/60">Audit score trend — sample</p>
        </motion.article>
        </div>
      </div>
    </div>
  </section>
);
